/*
 * Talking to /customers on the API, and remembering who is signed in.
 *
 * FETCH AND NOT AXIOS, DELIBERATELY. front/src/data/apiAuth.js monkey-patches
 * `axios.create` to attach the ADMINISTRATOR's token to every request bound
 * for our API - it has to, because twenty-two components each build their own
 * instance. A customer request made through axios would therefore carry an
 * admin token it never asked for. The customer routes reject it (different
 * signing key, different audience), so nothing would break; but sending an
 * administrator's credential to an endpoint that has no use for it is the
 * kind of thing that is fine until the day it is not.
 *
 * fetch is native, is not patched by anything, and needs no dependency -
 * which matters here, because adding one means editing front/package.json and
 * re-resolving a 2021 tree (CLAUDE.md section 4).
 *
 * THE TWO TOKENS ARE STORED UNDER DIFFERENT KEYS for the same reason they are
 * signed with different keys: so that neither can ever be mistaken for the
 * other. "user" is the admin's and belongs to apiAuth.js. Nothing here reads
 * or writes it.
 */

const STORAGE_KEY = "customer";

const base = () => String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

/* ------------------------------------------------------------ the session */

/**
 * The stored session, or null.
 *
 * NEVER THROWS. localStorage is unavailable in a private window, when site
 * data is blocked, and inside some embedded browsers - and an account page
 * that throws on load is worse than one that simply shows a sign-in form.
 */
export function currentSession() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.token !== "string" || !parsed.token) return null;
        return parsed;
    } catch (err) {
        return null;
    }
}

export function saveSession(session) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (err) {
        /*
         * Storage refused. The session still works for this page load,
         * because the caller holds it in state - they are simply signed out
         * when they navigate. Better than refusing to sign them in at all.
         */
    }
}

export function clearSession() {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        /* nothing to clear */
    }
}

/* ------------------------------------------------------------ the calls */

/**
 * One request, with the error messages the API already writes.
 *
 * THE SERVER'S WORDING IS USED AS-IS. Those sentences were written with some
 * care - "That email address and password do not match" is deliberately the
 * same for a wrong password and an address with no account - and inventing a
 * friendlier message here would undo that.
 */
async function call(path, { method = "POST", body, token } = {}) {
    const headers = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = "Bearer " + token;

    let res;
    try {
        res = await fetch(base() + "/customers" + path, {
            method: method,
            headers: headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    } catch (err) {
        /*
         * A network failure, not an answer. Saying "wrong password" here
         * would be a lie, and saying nothing leaves a spinner going.
         */
        throw new Error("Could not reach us. Check your connection and try again.");
    }

    let data = null;
    try {
        data = await res.json();
    } catch (err) {
        data = null;
    }

    if (!res.ok) {
        const message = (data && data.error) || "Something went wrong. Try again.";
        const error = new Error(message);
        error.status = res.status;
        throw error;
    }
    return data || {};
}

export const register = (email, password) =>
    call("/register", { body: { email: email, password: password } });

export const verify = (token) => call("/verify", { body: { token: token } });

export const signIn = (email, password) =>
    call("/sign-in", { body: { email: email, password: password } });

/**
 * Hands Google's ID token to the server, which is the only thing that can
 * check it.
 *
 * Nothing is read out of the credential here. It is a signed assertion, and
 * reading the email from it in the browser would be reading something an
 * attacker can simply write - see api/data/googleIdToken.js, which verifies
 * the signature, the audience and email_verified before believing a word.
 */
export const signInWithGoogle = (credential) =>
    call("/google", { body: { credential: credential } });

/**
 * Asks for a sign-in link.
 *
 * ALWAYS SUCCEEDS, by design. The server answers the same sentence whether
 * or not the address has an account, because whether it does is exactly what
 * must not be revealed - so the screen must not treat this as a yes/no
 * either.
 */
export const requestMagicLink = (email) => call("/magic", { body: { email: email } });

/** Spends a sign-in link. Single use. */
export const useMagicLink = (token) => call("/magic/use", { body: { token: token } });

/* ------------------------------------------------- signing in a phone */

/** What a waiting phone says it is, for the approval page. Needs no account. */
export const devicePending = (code) =>
    call("/device/pending?code=" + encodeURIComponent(code), { method: "GET" });

/**
 * Lets a phone in, or refuses it.
 *
 * SIGNED IN, because this grants a device access to THIS account. It is the
 * one step in the flow that has to be somebody.
 */
export const deviceApprove = (userCode, token, deny) =>
    call("/device/approve", { body: { userCode: userCode, deny: Boolean(deny) }, token: token });

export const forgot = (email) => call("/forgot", { body: { email: email } });

export const reset = (token, password) =>
    call("/reset", { body: { token: token, password: password } });

export const me = (token) => call("/me", { method: "GET", token: token });

/*
 * Saved properties.
 *
 * The account page said "saved properties stay on your phone until hosting
 * arrives" from the day it was written. This is that hosting, and the same
 * endpoints the Android app will post to.
 */
export const savedList = (token) => call("/saved", { method: "GET", token: token });

export const saveProperty = (token, property) =>
    call("/saved", { token: token, body: property });

export const removeSaved = (token, id) =>
    call("/saved/" + encodeURIComponent(id), { method: "DELETE", token: token });

/**
 * The same password rule the server enforces, so the form can say so before
 * a round trip.
 *
 * A COPY, AND KNOWINGLY SO. The server's copy in api/data/customerTokens.js
 * is the one that decides; this one only saves somebody a round trip to be
 * told something obvious. The two can drift, and the cost of drift is a
 * message that arrives later rather than sooner - which is why the server's
 * answer is always displayed when it disagrees.
 */
export function passwordProblem(raw) {
    const p = String(raw == null ? "" : raw);
    if (p.length < 12) return "Use at least 12 characters.";
    // bcrypt ignores anything past 72 BYTES, so the limit is bytes, not
    // characters - an emoji is four of them.
    if (new TextEncoder().encode(p).length > 72) return "That is too long - 72 bytes at most.";
    if (!p.trim()) return "That is only spaces.";
    return null;
}
